import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Bell, AlertTriangle, Radio, Hexagon, CheckCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { notificationNavigation } from "@/lib/notification-link";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type NotificationRow = {
  id: number;
  organization_id: string;
  source: string;
  source_id: string | null;
  event_type: string;
  severity: string;
  title: string;
  body: string | null;
  link: string | null;
  metadata: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
};

const sourceIcon = (source: string) => {
  switch (source) {
    case "incident": return AlertTriangle;
    case "dispatch": return Radio;
    case "geofence": return Hexagon;
    default: return Bell;
  }
};

const sevColor = (sev: string) =>
  sev === "critical" ? "text-destructive"
  : sev === "warning" ? "text-warning"
  : "text-primary";

const timeAgo = (iso: string) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export function NotificationsBell() {
  const { user, currentOrg } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [open, setOpen] = useState(false);

  const unread = items.filter((n) => !n.read_at).length;

  useEffect(() => {
    if (!user || !currentOrg) return;
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("organization_id", currentOrg.id)
        .eq("recipient_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);
      if (!cancelled && data) setItems(data as NotificationRow[]);
    };
    load();

    const channel = supabase
      .channel(`notif-${user.id}-${currentOrg.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `recipient_id=eq.${user.id}` },
        (payload) => {
          const row = payload.new as NotificationRow;
          if (row.organization_id !== currentOrg.id) return;
          setItems((prev) => [row, ...prev].slice(0, 20));
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications", filter: `recipient_id=eq.${user.id}` },
        (payload) => {
          const row = payload.new as NotificationRow;
          setItems((prev) => prev.map((n) => (n.id === row.id ? row : n)));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user, currentOrg]);

  const markOneRead = async (id: number) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    await supabase.from("notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
  };

  const markAllRead = async () => {
    if (!user || !currentOrg) return;
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    await supabase
      .from("notifications")
      .update({ read_at: now })
      .eq("recipient_id", user.id)
      .eq("organization_id", currentOrg.id)
      .is("read_at", null);
  };

  const handleClick = async (n: NotificationRow) => {
    if (!n.read_at) await markOneRead(n.id);
    setOpen(false);
    if (n.link) navigate(notificationNavigation(n.link) as never);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          <button
            onClick={markAllRead}
            disabled={unread === 0}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        </div>
        <DropdownMenuSeparator className="m-0" />
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              You're all caught up.
            </div>
          ) : (
            items.map((n) => {
              const Icon = sourceIcon(n.source);
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-accent/50 ${
                    !n.read_at ? "bg-primary/5" : ""
                  }`}
                >
                  <div className={`mt-0.5 ${sevColor(n.severity)}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium leading-tight">{n.title}</p>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                    </div>
                    {n.body && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
                    )}
                  </div>
                  {!n.read_at && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                </button>
              );
            })
          )}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <Link
          to="/notifications"
          onClick={() => setOpen(false)}
          className="block px-3 py-2 text-center text-xs font-medium text-primary hover:bg-accent/50"
        >
          View all notifications
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
